import { useState, useEffect } from 'react';
import { Brain, Sparkles, Database, BarChart3, Lightbulb } from 'lucide-react';

const stages = [
  { icon: Brain, text: 'Thinking', subtext: 'Understanding your question...' },
  { icon: Database, text: 'Querying', subtext: 'Fetching relevant data...' },
  { icon: BarChart3, text: 'Analyzing', subtext: 'Processing analytics...' },
  { icon: Lightbulb, text: 'Generating', subtext: 'Creating insights...' },
];

export default function ThinkingIndicator({ isActive, startTime }) {
  const [currentStage, setCurrentStage] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    if (!isActive) {
      setCurrentStage(0);
      setElapsedTime(0);
      return;
    }

    // Update elapsed time every 100ms
    const timeInterval = setInterval(() => {
      if (startTime) {
        setElapsedTime(Math.floor((Date.now() - startTime) / 100) / 10);
      }
    }, 100);

    // Cycle through stages
    const stageInterval = setInterval(() => {
      setCurrentStage(prev => (prev + 1) % stages.length);
    }, 2500);

    return () => {
      clearInterval(timeInterval);
      clearInterval(stageInterval);
    };
  }, [isActive, startTime]);

  if (!isActive) return null;

  const { icon: Icon, text, subtext } = stages[currentStage];

  return (
    <div className="flex gap-4 py-6" data-testid="thinking-indicator">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg shadow-primary/25">
        <Icon className="w-5 h-5 text-white animate-pulse" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-1">
          <span className="font-semibold text-primary">{text}</span>
          <span className="text-sm text-muted-foreground">{elapsedTime.toFixed(1)}s</span>
        </div>
        <p className="text-sm text-muted-foreground">{subtext}</p>
        
        {/* Progress bar */}
        <div className="mt-3 h-1.5 w-48 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-primary to-secondary rounded-full animate-pulse"
            style={{ 
              width: `${((currentStage + 1) / stages.length) * 100}%`,
              transition: 'width 0.5s ease-in-out'
            }}
          />
        </div>

        {/* Stage indicators */}
        <div className="flex gap-2 mt-3">
          {stages.map((stage, idx) => (
            <div 
              key={idx}
              className={`flex items-center gap-1.5 text-xs ${
                idx <= currentStage ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              <stage.icon className={`w-3 h-3 ${idx === currentStage ? 'animate-bounce' : ''}`} />
              <span className="hidden sm:inline">{stage.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
